import { matchPath } from "react-router-dom";
import { IndividualRoute, RoutePaths } from "../../enumStrings";
import { UserType } from "../../graphql/types";

export type CmpNavbarCanonicalRoute = IndividualRoute<UserType.ChamberAdmin>;

export const CMP_NAVBAR_GROUP_IDS = {
  chamber: "chamber",
  invoices: "invoices",
  members: "members",
  prospects: "prospects",
  events: "events",
  emails: "emails",
} as const;

export type CmpNavbarGroupId =
  typeof CMP_NAVBAR_GROUP_IDS[keyof typeof CMP_NAVBAR_GROUP_IDS];

// Claude Design (19): the old "Contact Support" footer item is replaced by the
// "Need Help?" Freshdesk link rendered by Navbar.tsx (the /support page stays
// reachable for old links but is not in the sidebar).
export const CMP_NAVBAR_FOOTER_SECTION_IDS = {
  products: "products",
} as const;

export type CmpNavbarFooterSectionId =
  typeof CMP_NAVBAR_FOOTER_SECTION_IDS[keyof typeof CMP_NAVBAR_FOOTER_SECTION_IDS];

export const CMP_NAVBAR_ITEM_IDS = {
  analyticsOverview: "chamber.analytics-overview",
  chamberSetup: "chamber.setup",
  manageChamberProfile: "chamber.manage-profile",
  calculators: "chamber.calculators",
  reports: "chamber.reports",
  createTicketInvoices: "invoices.create-ticket",
  createSponsorshipInvoices: "invoices.create-sponsorship",
  createAdvertisingInvoices: "invoices.create-advertising",
  createCustomInvoices: "invoices.create-custom",
  manageInvoices: "invoices.manage",
  createMember: "members.create",
  manageMembers: "members.manage",
  createProspect: "prospects.create",
  manageProspects: "prospects.manage",
  createEvent: "events.create",
  manageEvents: "events.manage",
  manageTickets: "events.manage-tickets",
  manageSponsorship: "events.manage-sponsorship",
  createEmail: "emails.create",
  manageEmails: "emails.manage",
  createSurvey: "emails.create-survey",
  manageSurveys: "emails.manage-surveys",
  websiteBuilder: "footer.website-builder",
  chamberPerksDashboard: "footer.chamber-perks-dashboard",
} as const;

export type CmpNavbarItemId =
  typeof CMP_NAVBAR_ITEM_IDS[keyof typeof CMP_NAVBAR_ITEM_IDS];

export type CmpNavbarIconId =
  | "home"
  | "manage-invoices"
  | "users"
  | "user-search"
  | "calendar"
  | "mail"
  | "monitor"
  | "chamber-perks"
  | "help-circle";

export type CmpNavbarDestination = Readonly<{
  pathname: RoutePaths;
  search?: string;
}>;

/**
 * What's New page key from the design navUpdates() (Monday 13282310441), and
 * how an update shows on the item: a 6px red dot, or the red "New" pill
 * (Calculators). Items with a badge (Coming Soon) never show an update.
 */
export type CmpNavbarUpdateMarker = "dot" | "new";

export type CmpNavbarItem = Readonly<{
  id: CmpNavbarItemId;
  label: string;
  icon?: CmpNavbarIconId;
  // Claude Design (16) sidebar pill, e.g. "Coming Soon" on the survey items.
  badge?: string;
  whatsNewKey?: string;
  updateMarker?: CmpNavbarUpdateMarker;
  route: CmpNavbarCanonicalRoute;
  destination: CmpNavbarDestination;
  isActive: boolean;
  // S10: shown greyed out because the employee lacks the permission.
  permissionLocked?: boolean;
}>;

export type CmpNavbarGroup = Readonly<{
  id: CmpNavbarGroupId;
  label: string;
  icon: CmpNavbarIconId;
  items: readonly CmpNavbarItem[];
}>;

export type CmpNavbarFooterSection = Readonly<{
  id: CmpNavbarFooterSectionId;
  dividerBefore: true;
  items: readonly CmpNavbarItem[];
}>;

export type CmpNavbarPresentation = Readonly<{
  mainGroups: readonly CmpNavbarGroup[];
  footerSections: readonly CmpNavbarFooterSection[];
}>;

type PathActivePolicy = Readonly<{
  kind: "path";
  aliases?: readonly RoutePaths[];
}>;

type NeverActivePolicy = Readonly<{
  kind: "never";
}>;

type QueryActivePolicy = Readonly<{
  kind: "query";
  parameter: string;
  value: string;
  validValues: readonly string[];
  defaultForInvalid?: boolean;
}>;

type ActivePolicy =
  | PathActivePolicy
  | NeverActivePolicy
  | QueryActivePolicy;

type ItemSpec = Readonly<{
  id: CmpNavbarItemId;
  label?: string;
  routePath: RoutePaths;
  search?: string;
  icon?: CmpNavbarIconId;
  badge?: string;
  whatsNewKey?: string;
  updateMarker?: CmpNavbarUpdateMarker;
  active?: ActivePolicy;
}>;

type MainGroupSpec = Readonly<{
  id: CmpNavbarGroupId;
  label: string;
  icon: CmpNavbarIconId;
  items: readonly ItemSpec[];
}>;

type FooterSectionSpec = Readonly<{
  id: CmpNavbarFooterSectionId;
  items: readonly ItemSpec[];
}>;

const surveyModeValues = ["create", "manage"] as const;

const mainGroupSpecs: readonly MainGroupSpec[] = [
  {
    id: CMP_NAVBAR_GROUP_IDS.chamber,
    label: "My Chamber",
    icon: "home",
    items: [
      {
        id: CMP_NAVBAR_ITEM_IDS.analyticsOverview,
        whatsNewKey: "analytics",
        routePath: RoutePaths.chamberAdmin_myChamber_analytics,
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.chamberSetup,
        whatsNewKey: "chamber-setup",
        routePath: RoutePaths.chamberAdmin_myChamber_chamberSettings,
        active: {
          kind: "path",
          aliases: [
            RoutePaths.chamberAdmin_myChamber_chamberSettingsStripeConnectCallback,
            RoutePaths.chamberAdmin_myChamber_createStaffUser,
          ],
        },
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.manageChamberProfile,
        whatsNewKey: "profile",
        routePath: RoutePaths.chamberAdmin_myChamber_manageChamber,
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.calculators,
        whatsNewKey: "calculators",
        updateMarker: "new",
        routePath: RoutePaths.chamberAdmin_calculators,
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.reports,
        whatsNewKey: "reports",
        routePath: RoutePaths.chamberAdmin_reports,
      },
    ],
  },
  {
    id: CMP_NAVBAR_GROUP_IDS.invoices,
    label: "Invoices & Payments",
    icon: "manage-invoices",
    items: [
      {
        id: CMP_NAVBAR_ITEM_IDS.createTicketInvoices,
        whatsNewKey: "create-ticket-invoice",
        label: "Create Ticket Invoices",
        routePath: RoutePaths.chamberAdmin_events_ticketSalesCreate,
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.createSponsorshipInvoices,
        whatsNewKey: "create-sponsorship-invoice",
        label: "Create Sponsorship Invoices",
        routePath: RoutePaths.chamberAdmin_events_sponsorshipSalesCreate,
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.createAdvertisingInvoices,
        whatsNewKey: "advertising",
        label: "Create Advertising Invoices",
        routePath: RoutePaths.chamberAdmin_invoicing_createAdvertising,
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.createCustomInvoices,
        whatsNewKey: "custom-invoice",
        label: "Create Custom Invoices",
        routePath: RoutePaths.chamberAdmin_invoicing_createCustom,
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.manageInvoices,
        whatsNewKey: "invoices-payments",
        label: "Manage all Invoices & Payments",
        routePath: RoutePaths.chamberAdmin_invoicing_manage,
        active: {
          kind: "path",
          aliases: [RoutePaths.chamberAdmin_invoicing_update],
        },
      },
    ],
  },
  {
    id: CMP_NAVBAR_GROUP_IDS.members,
    label: "Members",
    icon: "users",
    items: [
      {
        id: CMP_NAVBAR_ITEM_IDS.createMember,
        whatsNewKey: "create-member",
        label: "Create Member & Invoice",
        routePath: RoutePaths.chamberAdmin_members_create,
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.manageMembers,
        whatsNewKey: "manage-members",
        routePath: RoutePaths.chamberAdmin_members_manage,
        active: {
          kind: "path",
          aliases: [
            RoutePaths.chamberAdmin_members_update,
            RoutePaths.chamberAdmin_members_updateLegacy,
          ],
        },
      },
    ],
  },
  {
    id: CMP_NAVBAR_GROUP_IDS.prospects,
    label: "Prospect Leads",
    icon: "user-search",
    items: [
      {
        id: CMP_NAVBAR_ITEM_IDS.createProspect,
        whatsNewKey: "create-prospect",
        routePath: RoutePaths.chamberAdmin_prospects_create,
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.manageProspects,
        whatsNewKey: "manage-prospects",
        routePath: RoutePaths.chamberAdmin_prospects_manage,
        active: {
          kind: "path",
          aliases: [RoutePaths.chamberAdmin_prospects_update],
        },
      },
    ],
  },
  {
    id: CMP_NAVBAR_GROUP_IDS.events,
    label: "Events & Sponsorships",
    icon: "calendar",
    items: [
      {
        id: CMP_NAVBAR_ITEM_IDS.createEvent,
        whatsNewKey: "create-event",
        routePath: RoutePaths.chamberAdmin_events_create,
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.manageEvents,
        whatsNewKey: "manage-events",
        routePath: RoutePaths.chamberAdmin_events_manage,
        active: {
          kind: "path",
          aliases: [RoutePaths.chamberAdmin_events_update],
        },
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.manageTickets,
        whatsNewKey: "manage-tickets",
        label: "Manage Tickets",
        routePath: RoutePaths.chamberAdmin_events_ticketSalesManage,
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.manageSponsorship,
        whatsNewKey: "manage-sponsorship",
        label: "Manage Sponsorship",
        routePath: RoutePaths.chamberAdmin_events_sponsorshipSalesManage,
      },
    ],
  },
  {
    id: CMP_NAVBAR_GROUP_IDS.emails,
    label: "Emails & Communications",
    icon: "mail",
    items: [
      {
        id: CMP_NAVBAR_ITEM_IDS.createEmail,
        whatsNewKey: "create-emails",
        label: "Create Emails & Communications",
        routePath: RoutePaths.chamberAdmin_emails_create,
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.manageEmails,
        whatsNewKey: "manage-emails",
        label: "Manage Emails & Communications",
        routePath: RoutePaths.chamberAdmin_emails_manage,
        active: {
          kind: "path",
          aliases: [RoutePaths.chamberAdmin_emails_update],
        },
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.createSurvey,
        label: "Create Survey",
        badge: "Coming Soon",
        routePath: RoutePaths.chamberAdmin_surveyEngine,
        search: "?mode=create",
        active: {
          kind: "query",
          parameter: "mode",
          value: "create",
          validValues: surveyModeValues,
          defaultForInvalid: true,
        },
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.manageSurveys,
        label: "Manage Surveys",
        badge: "Coming Soon",
        routePath: RoutePaths.chamberAdmin_surveyEngine,
        search: "?mode=manage",
        active: {
          kind: "query",
          parameter: "mode",
          value: "manage",
          validValues: surveyModeValues,
        },
      },
    ],
  },
];

const footerSectionSpecs: readonly FooterSectionSpec[] = [
  {
    id: CMP_NAVBAR_FOOTER_SECTION_IDS.products,
    items: [
      {
        id: CMP_NAVBAR_ITEM_IDS.websiteBuilder,
        icon: "monitor",
        routePath: RoutePaths.chamberAdmin_website_builder,
      },
      {
        id: CMP_NAVBAR_ITEM_IDS.chamberPerksDashboard,
        label: "Chamber Perks App™ Dashboard",
        icon: "chamber-perks",
        routePath: RoutePaths.chamberAdmin_perks,
        active: { kind: "never" },
      },
    ],
  },
];

const doesPathMatch = (pattern: RoutePaths, pathname: string): boolean =>
  Boolean(matchPath({ path: pattern, end: true }, pathname));

const isItemActive = ({
  spec,
  pathname,
  routeMap,
  search,
}: {
  spec: ItemSpec;
  pathname: string;
  routeMap: ReadonlyMap<RoutePaths, CmpNavbarCanonicalRoute>;
  search: string;
}): boolean => {
  const policy = spec.active || { kind: "path" };

  if (policy.kind === "never") {
    return false;
  }

  if (!doesPathMatch(spec.routePath, pathname)) {
    if (policy.kind !== "path") {
      return false;
    }

    return Boolean(
      policy.aliases?.some(
        (aliasPath) =>
          routeMap.has(aliasPath) && doesPathMatch(aliasPath, pathname),
      ),
    );
  }

  if (policy.kind === "path") {
    return true;
  }

  const selectedValue = new URLSearchParams(search).get(policy.parameter);

  if (selectedValue === policy.value) {
    return true;
  }

  return Boolean(
    policy.defaultForInvalid &&
      !policy.validValues.some((validValue) => validValue === selectedValue)
  );
};

const buildRouteMap = (
  allowedRoutes: readonly CmpNavbarCanonicalRoute[]
): Map<RoutePaths, CmpNavbarCanonicalRoute> => {
  const routeMap = new Map<RoutePaths, CmpNavbarCanonicalRoute>();

  allowedRoutes.forEach((route) => {
    if (routeMap.has(route.path)) {
      throw new Error(
        `CMP navbar received more than one canonical route for "${route.path}".`
      );
    }

    routeMap.set(route.path, route);
  });

  return routeMap;
};

const buildItem = ({
  spec,
  routeMap,
  lockedRouteMap,
  pathname,
  search,
}: {
  spec: ItemSpec;
  routeMap: ReadonlyMap<RoutePaths, CmpNavbarCanonicalRoute>;
  lockedRouteMap: ReadonlyMap<RoutePaths, CmpNavbarCanonicalRoute>;
  pathname: string;
  search: string;
}): CmpNavbarItem | null => {
  const lockedRoute = routeMap.has(spec.routePath)
    ? undefined
    : lockedRouteMap.get(spec.routePath);
  const route = routeMap.get(spec.routePath) || lockedRoute;

  if (!route || route.hideInNavBar) {
    return null;
  }

  if (lockedRoute) {
    return {
      id: spec.id,
      label: spec.label ?? route.navTitle ?? route.title,
      icon: spec.icon,
      route,
      destination: { pathname: spec.routePath },
      isActive: false,
      permissionLocked: true,
    };
  }

  return {
    id: spec.id,
    label: spec.label ?? route.navTitle ?? route.title,
    icon: spec.icon,
    ...(spec.badge ? { badge: spec.badge } : {}),
    // Design rule: never an update marker on Coming Soon items.
    ...(spec.whatsNewKey && !spec.badge
      ? { whatsNewKey: spec.whatsNewKey, updateMarker: spec.updateMarker ?? "dot" }
      : {}),
    route,
    destination: {
      pathname: spec.routePath,
      ...(spec.search ? { search: spec.search } : {}),
    },
    isActive: isItemActive({ spec, pathname, routeMap, search }),
  };
};

export const buildCmpNavbarPresentation = ({
  allowedRoutes,
  permissionLockedRoutes = [],
  chamberName,
  pathname,
  search = "",
}: {
  allowedRoutes: readonly CmpNavbarCanonicalRoute[];
  // Routes shown greyed out (isCmpExternalRoutePermissionLocked).
  permissionLockedRoutes?: readonly CmpNavbarCanonicalRoute[];
  chamberName?: string | null;
  pathname: string;
  search?: string;
}): CmpNavbarPresentation => {
  const routeMap = buildRouteMap(allowedRoutes);
  const lockedRouteMap = buildRouteMap(permissionLockedRoutes);
  const buildVisibleItems = (items: readonly ItemSpec[]) =>
    items.reduce<CmpNavbarItem[]>((visibleItems, spec) => {
      const item = buildItem({ spec, routeMap, lockedRouteMap, pathname, search });

      if (item) {
        visibleItems.push(item);
      }

      return visibleItems;
    }, []);

  const normalizedChamberName = chamberName?.trim() || "My Chamber";
  const mainGroups = mainGroupSpecs.reduce<CmpNavbarGroup[]>(
    (visibleGroups, group) => {
      const items = buildVisibleItems(group.items);

      if (items.length) {
        visibleGroups.push({
          id: group.id,
          label:
            group.id === CMP_NAVBAR_GROUP_IDS.chamber
              ? normalizedChamberName
              : group.label,
          icon: group.icon,
          items,
        });
      }

      return visibleGroups;
    },
    []
  );
  const footerSections = footerSectionSpecs.reduce<CmpNavbarFooterSection[]>(
    (visibleSections, section) => {
      const items = buildVisibleItems(section.items);

      if (items.length) {
        visibleSections.push({
          id: section.id,
          dividerBefore: true,
          items,
        });
      }

      return visibleSections;
    },
    []
  );

  return { mainGroups, footerSections };
};
